import { ChangeEvent, FC, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AxiosError } from 'axios';
import { Box, Button, TextField } from '@mui/material';
import { DataGrid, GridPaginationModel, GridSortDirection, GridSortModel, useGridApiRef } from '@mui/x-data-grid';
import type { GridColDef } from '@mui/x-data-grid/models/colDef/gridColDef';
import toastService from 'components/elements/Toastify';
import { ManageStreetModal } from 'components/modules/ManageStreetModal';
import { RejectedDataProcessButton } from 'components/modules/RejectedDataProcessButton';
import { UpdateStreetAnchor } from 'components/modules/UpdateStreetAnchor';
import { UtilityMapProcessingWarning } from 'components/modules/UtilityMapProcessingWarning';
import { ROUTES } from 'routes';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { clearStreetsData, getStreets, getStreetsIsLoadingSelector, getStreetsSelector } from 'store/streets';
import { clearStreetTypesData, getStreetsTypes } from 'store/streets-types';
import { IErrorResponse, IQuery, IStreetFormValues, IStreetsDetails } from 'types';
import { PAGINATION_LIMIT } from 'utils/constants';
import { Rights } from 'utils/enums/rights.enum';
import { DATE_FORMAT, SORT_DIR, STREETS_FIELDS_NAME } from 'utils/enums';
import { formatQueryString, getErrorMessage, isUtilityMapProcessRunning, parseQueryString } from 'utils/helpers';
import { formatDateView } from 'utils/helpers/date';
import { getTableSetting } from 'utils/helpers/table-setting';
import { useCityAutocomplete, useDebounce, useModal, useUtilityMapStatus } from 'utils/hooks';
import { usePermissions } from 'utils/hooks/use-permissions';
import { StreetsService } from 'utils/services';
import { RejectedDataService } from 'utils/services/rejected.service';
import { COLUMNS_CONFIG } from './config';
import s from './index.module.scss';

const DEVICE_FP = '6037e65eefbdasdfadsfasdf3';
const CITY_SEARCH_DEBOUNCE = 500;

const INITIAL_VALUES: IStreetFormValues = {
	name: '',
	streetTypeId: '',
	cityId: '',
};

export const StreetsPage: FC = () => {
	const { search } = useLocation();
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const checkRights = usePermissions();
	const streets = useAppSelector(getStreetsSelector);
	const isLoading = useAppSelector(getStreetsIsLoadingSelector);
	const apiRef = useGridApiRef();
	const [columnWidths, setColumnWidths] = useState({});
	const [isCreateModalOpen, handleOpenCreateModal, handleCloseCreateModal] = useModal();
	const { markRunning: markUtilityMapRunning, status: utilityMapStatus } = useUtilityMapStatus('byStreet');
	const {
		cityOptions,
		handleLoadMoreCities,
		handleSearchCities,
		hasMoreCityOptions,
		isCityOptionsLoading,
		resetCityAutocomplete,
	} = useCityAutocomplete({ deviceFP: DEVICE_FP });

	const query = useMemo(() => parseQueryString<IQuery>(search, { parseBooleans: true, parseNumbers: true }), [search]);
	const [citySearch, setCitySearch] = useState(query.city || '');
	const debouncedCitySearch = useDebounce(citySearch.trim(), CITY_SEARCH_DEBOUNCE);

	const requestBody = useMemo(
		() => ({
			offset: (query.page || 0) * (query.pageSize || 0),
			limit: query.pageSize || PAGINATION_LIMIT.SMALL,
			orderBy: [
				{
					field: query.field || STREETS_FIELDS_NAME.NAME,
					direction: query.sort || SORT_DIR.ASC,
				},
			],
			searchBy: {
				name: query.searchQuery,
				city: query.city,
			},
			deviceFP: DEVICE_FP,
		}),
		[query]
	);

	useEffect(() => {
		dispatch(getStreets(requestBody));
	}, [dispatch, requestBody]);

	useEffect(() => {
		setCitySearch(query.city || '');
	}, [query.city]);

	useEffect(() => {
		if (citySearch.trim() !== debouncedCitySearch) {
			return;
		}

		if (debouncedCitySearch === (query.city || '')) {
			return;
		}

		navigate(
			{
				search: formatQueryString({
					...query,
					city: debouncedCitySearch || undefined,
					page: 0,
				}),
			},
			{ replace: true }
		);
	}, [citySearch, debouncedCitySearch, navigate, query]);

	useEffect(() => {
		return () => {
			dispatch(clearStreetsData());
			dispatch(clearStreetTypesData());
		};
	}, [dispatch]);

	useEffect(() => {
		dispatch(
			getStreetsTypes({
				offset: 0,
				limit: PAGINATION_LIMIT.BIG,
				deviceFP: DEVICE_FP,
			})
		);
	}, [dispatch]);

	useEffect(() => {
		setColumnWidths(getTableSetting(ROUTES.streets));
	}, [requestBody]);

	COLUMNS_CONFIG.forEach((item: GridColDef) => {
		// @ts-ignore
		if (columnWidths && columnWidths[item.field]) {
			// @ts-ignore
			item.width = columnWidths[item.field];
			item.flex = undefined;
		}
	});

	const handleColumnResize = (params: any) => {
		const newWidths = {
			...columnWidths,
			[params.colDef.field]: params.width,
		};
		setColumnWidths(newWidths);
		localStorage.setItem(ROUTES.streets, JSON.stringify(newWidths));
	};

	const handleChangePage = ({ page, pageSize }: GridPaginationModel): void => {
		navigate(
			{
				search: formatQueryString({
					...query,
					page,
					pageSize,
				}),
			},
			{ replace: true }
		);
	};

	const handleSortField = (model: GridSortModel): void => {
		const { field, sort } = model[0] || {};
		navigate(
			{
				search: formatQueryString({
					...query,
					field,
					sort,
					page: 0,
				}),
			},
			{ replace: true }
		);
	};

	const handleChangeCitySearch = (event: ChangeEvent<HTMLInputElement>): void => {
		setCitySearch(event.target.value);
	};

	const handleClearSearch = (): void => {
		setCitySearch('');
		navigate(
			{
				search: formatQueryString({
					...query,
					searchQuery: undefined,
					city: undefined,
					page: 0,
				}),
			},
			{ replace: true }
		);
	};

	const handleCreateStreet = async (values: IStreetFormValues) => {
		try {
			await StreetsService.createStreets({
				name: values.name.trim(),
				streetTypeId: values.streetTypeId,
				cityId: Number(values.cityId),
				deviceFP: DEVICE_FP,
			});
			handleCloseCreateModal();
			toastService.success('Вулицю успішно створено');
			dispatch(getStreets(requestBody));
		} catch (error) {
			toastService.error(getErrorMessage(error as AxiosError<IErrorResponse>));
		}
	};

	const rows = useMemo(
		() =>
			streets?.results.map((street) => ({
				...street,
				createdAt: formatDateView(street.createdAt, DATE_FORMAT.DATE_FULL),
				updatedAt: formatDateView(street.updatedAt, DATE_FORMAT.DATE_FULL),
			})),
		[streets]
	);

	const columns = useMemo<GridColDef<IStreetsDetails>[]>(
		() =>
			checkRights(Rights.STREETS_MANAGE)
				? [
						...COLUMNS_CONFIG,
						{
							field: 'processRejectedData',
							headerName: 'Реджектед',
							sortable: false,
							align: 'center',
							headerAlign: 'center',
							width: 220,
							renderCell: (params) => (
								<RejectedDataProcessButton
									confirmTitle={`Запустити обробку реджектед для вулиці ${params.row.name}?`}
									confirmMessage="Буде запущено фонову обробку реджектед-даних для цієї вулиці."
									infoMessage="Запуск виконано. Перевірте через 5 хвилин"
									disabled={!utilityMapStatus || isUtilityMapProcessRunning(utilityMapStatus)}
									onProcess={() => RejectedDataService.mapRejectedDataByStreetId({ streetId: params.row.id })}
									onProcessSuccess={() => {
										markUtilityMapRunning('byStreet');
									}}
								/>
							),
						},
						{
							field: 'edit',
							headerName: ' ',
							sortable: false,
							align: 'center',
							width: 60,
							renderCell: (params) => (
								<UpdateStreetAnchor
									streetRow={params.row}
									cityOptions={cityOptions}
									cityOptionsLoading={isCityOptionsLoading}
									onCitySearch={handleSearchCities}
									onCityLoadMore={handleLoadMoreCities}
									hasMoreCityOptions={hasMoreCityOptions}
								/>
							),
						},
				  ]
				: COLUMNS_CONFIG,
		[
			checkRights,
			cityOptions,
			isCityOptionsLoading,
			handleSearchCities,
			handleLoadMoreCities,
			hasMoreCityOptions,
			markUtilityMapRunning,
			utilityMapStatus,
		]
	);

	return (
		<Box sx={{ display: 'grid', gap: 2, height: '100%' }}>
			<UtilityMapProcessingWarning status={utilityMapStatus} />
			<Box className={s.toolbar}>
				<Box className={s.filter}>
					<TextField value={citySearch} placeholder="Пошук по місту" onChange={handleChangeCitySearch} />
					<Button variant="outlined" onClick={handleClearSearch}>
						Очистити
					</Button>
				</Box>

				{checkRights(Rights.STREETS_MANAGE) && (
					<Button
						variant="contained"
						onClick={() => {
							resetCityAutocomplete();
							handleOpenCreateModal();
						}}
					>
						Створити
					</Button>
				)}
			</Box>

			<Box className={s.tableWrapper}>
				<DataGrid
					onColumnResize={handleColumnResize}
					apiRef={apiRef}
					disableColumnFilter
					disableRowSelectionOnClick
					rows={rows || []}
					columns={columns}
					rowCount={streets?.total || 0}
					loading={isLoading}
					initialState={{
						pagination: {
							paginationModel: { page: query.page || 0, pageSize: query.pageSize || PAGINATION_LIMIT.SMALL },
						},
						sorting: {
							sortModel: [{ field: query.field || STREETS_FIELDS_NAME.CREATE_AT, sort: query.sort as GridSortDirection }],
						},
					}}
					slotProps={{
						pagination: {
							labelRowsPerPage: 'Показувати :',
							page: query.page || 0,
							rowsPerPage: query.pageSize || PAGINATION_LIMIT.SMALL,
						},
					}}
					paginationMode="server"
					sortingMode="server"
					onSortModelChange={handleSortField}
					onPaginationModelChange={handleChangePage}
					className={s.table}
					pageSizeOptions={[PAGINATION_LIMIT.SMALL, PAGINATION_LIMIT.MIDDLE, PAGINATION_LIMIT.BIG]}
				/>
			</Box>

			<ManageStreetModal
				title="Створити вулицю"
				isOpen={isCreateModalOpen}
				onClose={handleCloseCreateModal}
				initialValues={INITIAL_VALUES}
				onSubmit={handleCreateStreet}
				cityOptions={cityOptions}
				cityOptionsLoading={isCityOptionsLoading}
				onCitySearch={handleSearchCities}
				onCityLoadMore={handleLoadMoreCities}
				hasMoreCityOptions={hasMoreCityOptions}
				buttonText="Створити"
			/>
		</Box>
	);
};
